import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, Crown, LogIn } from "lucide-react";
import { toast } from "sonner";
import { fetchPlans, subscribe, chooseFree, useSub } from "@/lib/subscription";
import { useAuth } from "@/lib/auth";
import { ReceiptModal } from "@/components/UpgradeGate";
import { getState } from "@/lib/progress";
import { FREE_SECTIONS, FREE_GAMES } from "@/lib/gating";

const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

export default function Pricing() {
  const { sub, setSub } = useSub();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    fetchPlans().then(d => setPlans(d.plans)).catch(() => setError("We couldn't load the plans right now. Please try again in a moment."));
  }, []);

  const active = sub.status === "active";
  const pick = async (plan) => {
    setBusy(plan.id);
    try {
      if (plan.amount === 0) { if (active) return; setSub(chooseFree()); toast.success("You're on the Free plan. Enjoy exploring!"); return; }
      const result = await subscribe(plan.id, getState().profile?.name);
      if (result) {
        setSub({ plan: result.plan, status: result.status, ends_at: result.ends_at, started_at: result.started_at });
        toast.success(`Welcome to Little Learner ${plan.name}! 🎉 Full access unlocked.`);
        if (result.receipt) setReceipt(result.receipt);
      }
    } catch (e) { toast.error(/fetch/i.test(e.message || "") ? "We couldn't reach the payment service. Please check your connection and try again." : (e.message || "Payment didn't go through. Please try again.")); }
    finally { setBusy(null); }
  };

  const endsAt = sub.ends_at ? new Date(sub.ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

  return (
    <div className="ll-bg min-h-screen pb-28 md:pb-12">
      <ReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="text-center pt-4 max-w-2xl mx-auto">
          <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">For Parents · Plans & Pricing</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-purple-900 mt-1">Pick the plan that fits your family</h1>
          <p className="text-purple-700/70 font-semibold mt-3">Start free, upgrade anytime. Every paid plan unlocks all 10 learning categories, 50+ activities and every mini-game.</p>
        </div>

        <div className={`ll-card mt-6 px-5 py-3 flex flex-wrap items-center justify-center gap-2 text-sm font-bold ${active ? "!bg-emerald-50 !border-emerald-300 text-emerald-800" : "text-purple-800"}`} data-testid="current-plan">
          <span className="text-lg">{active ? "🌟" : "🎈"}</span>
          {active ? <>You're on the <span className="capitalize">{sub.plan}</span>&nbsp;plan · full access until {endsAt}</> : <>You're on the Free plan · {FREE_SECTIONS.length} learning sections + {FREE_GAMES.length} games unlocked</>}
        </div>

        {!user && (
          <div className="ll-card mt-4 px-5 py-3 flex flex-wrap items-center justify-center gap-2 text-sm font-bold text-purple-800 bg-purple-50 border-purple-200" data-testid="restore-banner">
            <span>Already subscribed on another device?</span>
            <Link to="/parent" data-testid="restore-login-link" className="inline-flex items-center gap-1 text-purple-700 underline"><LogIn size={15} /> Log in to restore your plan</Link>
          </div>
        )}

        {error && <div className="ll-card mt-4 p-4 bg-pink-50 border-pink-200 text-purple-900 font-bold text-center" data-testid="plans-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mt-8 items-stretch">
          {plans.map(p => <PlanCard key={p.id} plan={p} current={active ? sub.plan === p.id : p.id === "free"} locked={active && p.id === "free"} busy={busy === p.id} onPick={() => pick(p)} />)}
        </div>

        <p className="text-center text-purple-600/80 text-sm font-semibold mt-8 max-w-xl mx-auto" data-testid="payment-methods-note">
          <span className="inline-flex items-center gap-1.5 bg-purple-100 text-purple-800 font-bold px-3 py-1 rounded-full">📱 UPI ready — GPay, PhonePe, Paytm & more</span><br className="sm:hidden" />
          <span className="block mt-2">Secure payments by Razorpay · UPI, cards, net banking & wallets · Prices in Indian Rupees (₹), taxes included.</span>
        </p>
      </main>
    </div>
  );
}

function PlanCard({ plan, current, locked, busy, onPick }) {
  const best = plan.best;
  const monthlyTotal = 9900 * 12;
  return (
    <div data-testid={`plan-card-${plan.id}`}
      className={`relative ll-card p-6 sm:p-7 flex flex-col ${best ? "bg-gradient-to-br from-violet-500 to-purple-600 text-white border-purple-400 md:-mt-4 md:mb-[-16px] shadow-2xl ring-4 ring-yellow-300" : "bg-white"}`}>
      {best && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-300 text-purple-900 font-display font-bold text-sm px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 whitespace-nowrap" data-testid="best-value-badge">
          <Crown size={16} /><span>Best Value</span>
        </div>
      )}
      <div className={`text-xs uppercase tracking-widest font-bold ${best ? "text-yellow-300" : "text-purple-500"}`}>{best ? "Recommended" : plan.tagline}</div>
      <h2 className="font-display font-bold text-3xl mt-1">{plan.name}</h2>
      <div className="mt-4 flex items-end gap-1">
        <span className="font-display font-bold text-5xl leading-none" data-testid={`plan-price-${plan.id}`}>{plan.amount ? inr(plan.amount) : "₹0"}</span>
        <span className={`font-bold pb-1 ${best ? "text-white/80" : "text-purple-500"}`}>/{plan.period === "forever" ? "free" : plan.period}</span>
      </div>
      {best && (
        <div className="mt-3 inline-flex items-center gap-2 bg-white/20 rounded-2xl px-3 py-2 text-sm font-bold" data-testid="yearly-savings">
          <Sparkles size={16} className="text-yellow-300" />
          Save {inr(plan.savings)} vs {inr(monthlyTotal)} paid monthly · that's ~₹{Math.round(plan.amount / 12 / 100)}/month
        </div>
      )}
      {plan.id === "monthly" && <div className="mt-3 text-sm font-bold text-purple-500">Billed monthly · cancel anytime</div>}
      {plan.id === "free" && <div className="mt-3 text-sm font-bold text-purple-500">No card needed</div>}
      <ul className="mt-5 space-y-2.5 flex-1">
        {plan.features.map(f => (
          <li key={f} className="flex items-start gap-2 text-sm font-semibold">
            <span className={`mt-0.5 rounded-full p-0.5 ${best ? "bg-yellow-300 text-purple-900" : "bg-emerald-100 text-emerald-700"}`}><Check size={14} strokeWidth={3} /></span>
            <span className={best ? "text-white/95" : "text-purple-800"}>{f}</span>
          </li>
        ))}
      </ul>
      <button data-testid={`plan-cta-${plan.id}`} onClick={onPick} disabled={busy || current || locked}
        className={`ll-btn mt-6 w-full text-lg ${best ? (current ? "bg-white text-purple-700 shadow-[0_6px_0_0_#D8B4FE]" : "bg-yellow-300 text-purple-900 shadow-[0_6px_0_0_#CA8A04]") : "ll-btn-primary"} ${current && !best ? "opacity-60" : ""} ${locked ? "opacity-50" : ""}`}>
        {busy ? "Opening secure checkout…" : current ? "Current plan ✓" : locked ? "Included in your plan" : plan.cta}
      </button>
      {plan.amount > 0 && !current && (
        <div className={`mt-2 text-center text-xs font-bold ${best ? "text-white/85" : "text-purple-500"}`} data-testid={`plan-upi-hint-${plan.id}`}>📱 Pay with UPI, card, net banking or wallet</div>
      )}
    </div>
  );
}
